// src/components/profile-detail/tabs/OverviewTab.jsx
import React from "react";
import {
  Grid, Box, Typography, Chip, Stack, Paper, Avatar, useTheme, useMediaQuery
} from "@mui/material";
import CakeIcon from '@mui/icons-material/Cake';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import FlagIcon from '@mui/icons-material/Flag';
import TranslateIcon from '@mui/icons-material/Translate';
import HeightIcon from '@mui/icons-material/Height';
import ScaleIcon from '@mui/icons-material/Scale';
import BloodtypeIcon from '@mui/icons-material/Bloodtype';
import PersonIcon from '@mui/icons-material/Person';
import FemaleIcon from '@mui/icons-material/Female';
import MaleIcon from '@mui/icons-material/Male';
import LanguageIcon from '@mui/icons-material/Language';
import ChurchIcon from '@mui/icons-material/Church';
import InfoCard from "../profile-detail/components/InfoCard";

const IconBox = ({ icon, theme }) => (
  <Box sx={{ width: 40, height: 40, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'primary.alpha20' }}>
    {icon}
  </Box>
);

const SmallIconBox = ({ icon, theme }) => (
  <Box sx={{ width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'primary.alpha20', mb: 1, mx: 'auto' }}>
    {icon}
  </Box>
);

const InfoRow = ({ icon, label, value }) => {
  const theme = useTheme();
  return (
    <Stack direction="row" spacing={2} alignItems="center" sx={{ p: 2, borderRadius: 2, bgcolor: 'primary.alpha10', border: 1, borderColor: 'divider', mb: 1.5 }}>
      <IconBox icon={React.cloneElement(icon, { sx: { color: 'primary.light' } })} theme={theme} />
      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>{label}</Typography>
        <Typography variant="body1" color="text.primary" sx={{ fontWeight: 600 }}>{value || '-'}</Typography>
      </Box>
    </Stack>
  );
};

const getGender = (p, g) => {
  if (g?.icon) return g.icon;
  const gen = p?.personal_details?.gender?.toLowerCase();
  if (gen?.[0] === 'f') return <FemaleIcon />;
  if (gen?.[0] === 'm') return <MaleIcon />;
  return <PersonIcon />;
};

const getGenderText = (p, g) => {
  if (g?.text && g.text !== 'Not specified') return g.text;
  const gen = p?.personal_details?.gender?.toLowerCase();
  if (gen?.[0] === 'f') return 'Female';
  if (gen?.[0] === 'm') return 'Male';
  return 'Not specified';
};

const OverviewTab = ({ profile, genderInfo }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const genderIcon = getGender(profile, genderInfo);
  const genderText = getGenderText(profile, genderInfo);

  const Section = ({ title, icon, children, ...props }) => (
    <InfoCard title={title} icon={icon} sx={{ bgcolor: 'background.default', border: 1, borderColor: 'divider', height: '100%', ...props }}>
      {children}
    </InfoCard>
  );

  return (
    <Grid container spacing={isMobile ? 2 : 3}>
      <Grid item xs={12} md={6}>
        <Section title="Birth & Nationality" icon={<LocationOnIcon />}>
          {[
            { icon: <CakeIcon />, label: "Age", value: profile?.age && `${profile.age} years` },
            { icon: <LocationOnIcon />, label: "Place of Birth", value: profile?.place_of_birth },
            { icon: <FlagIcon />, label: "Nationality", value: profile?.nationality },
            { icon: <TranslateIcon />, label: "Mother Tongue", value: profile?.mother_tongue },
            /* { icon: genderIcon, label: "Gender", value: genderText } */
          ].map((item, i) => <InfoRow key={i} {...item} />)}
        </Section>
      </Grid>

      <Grid item xs={12} md={6}>
        <Section title="Physical Attributes" icon={<PersonIcon />}>
          {[
            { icon: <HeightIcon />, label: "Height", value: profile?.height },
            { icon: <ScaleIcon />, label: "Weight", value: profile?.weight_kg && `${profile.weight_kg} kg` },
            { icon: <BloodtypeIcon />, label: "Blood Group", value: profile?.blood_group }
          ].map((item, i) => <InfoRow key={i} {...item} />)}
        </Section>
      </Grid>

      <Grid item xs={12} md={6}>
        <Section title="Languages Known" icon={<LanguageIcon />} sx={{ minHeight: 320 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, justifyContent: 'center', p: 2, mb: 2 }}>
            {profile?.languages_known?.length ? profile.languages_known.map((lang, i) => (
              <Chip key={i} label={lang} icon={<LanguageIcon />} size="small" sx={{ bgcolor: 'primary.alpha20', color: 'primary.light', fontWeight: 600, border: 1, borderColor: 'divider' }} />
            )) : (
              <Typography color="text.secondary" fontStyle="italic">No languages specified</Typography>
            )}
          </Box>
          {profile?.caste && (
            <Paper sx={{ p: 2, borderRadius: 2, bgcolor: 'primary.alpha10', border: 1, borderColor: 'divider', textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">Caste</Typography>
              <Typography variant="body2" fontWeight={600} color="text.primary">{profile.caste}</Typography>
            </Paper>
          )}
        </Section>
      </Grid>

      <Grid item xs={12} md={6}>
        <Section title="Religious Details" icon={<ChurchIcon />} sx={{ minHeight: 320 }}>
          <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
            {[
              { icon: <ChurchIcon />, title: "Religion", value: profile?.religion },
              { icon: <PersonIcon />, title: "Sect", value: profile?.sect }
            ].map((item, i) => (
              <Paper key={i} sx={{ flex: 1, p: 2, borderRadius: 2, bgcolor: 'primary.alpha10', border: 1, borderColor: 'divider', textAlign: 'center' }}>
                <SmallIconBox icon={item.icon} theme={theme} />
                <Typography variant="caption" color="text.secondary">{item.title}</Typography>
                <Typography variant="body2" fontWeight={700} color="text.primary">{item.value || '-'}</Typography>
              </Paper>
            ))}
          </Box>
          {profile?.religious_practice_level && (
            <Paper sx={{ p: 2, borderRadius: 2, bgcolor: 'primary.alpha10', border: 1, borderColor: 'divider' }}>
              <Typography variant="caption" color="text.secondary">Practice Level</Typography>
              <Typography variant="body2" fontWeight={700} mb={1} color="text.primary">{profile.religious_practice_level}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ flex: 1, height: 6, bgcolor: 'primary.alpha20', borderRadius: 5, overflow: 'hidden' }}>
                  <Box sx={{ width: '70%', height: '100%', bgcolor: 'primary.light', borderRadius: 5 }} />
                </Box>
                <Typography variant="caption" color="primary.light" fontWeight={700}>70%</Typography>
              </Box>
            </Paper>
          )}
        </Section>
      </Grid>

      <Grid item xs={12}>
        <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, bgcolor: 'primary.alpha10', border: 1, borderColor: 'divider' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar sx={{ bgcolor: 'primary.alpha20', color: 'primary.light', mr: 2 }}><PersonIcon /></Avatar>
                <Box>
                  <Typography variant="h6" fontWeight={700} color="text.primary">Personal Summary</Typography>
                  <Typography variant="caption" color="text.secondary">At a glance</Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={8}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, justifyContent: { xs: 'center', md: 'flex-end' } }}>
                {[
                  { icon: <CakeIcon />, label: profile?.age && `${profile.age} years` },
                  /* { icon: genderIcon, label: genderText }, */
                  { icon: <FlagIcon />, label: profile?.nationality },
                  { icon: <ChurchIcon />, label: profile?.religion }
                ].filter(i => i.label).map((item, i) => (
                  <Chip key={i} label={item.label} icon={item.icon} size="small" sx={{ bgcolor: 'primary.alpha20', color: 'primary.light', fontWeight: 600, border: 1, borderColor: 'divider' }} />
                ))}
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default OverviewTab;